import { Injectable, inject } from '@angular/core';
import { FollowUpDetailsComponent } from '@app/modules/follow-ups/components/follow-up-details/follow-up-details.component';
import { FollowUpSearchComponent } from '@app/modules/follow-ups/components/follow-up-search/follow-up-search.component';
import { FollowUpSetAssigneeComponent } from '@app/modules/follow-ups/components/follow-up-set-assignee/follow-up-set-assignee.component';
import { FollowUpSetNotesComponent } from '@app/modules/follow-ups/components/follow-up-set-notes/follow-up-set-notes.component';
import { FollowUpsCreateDialogComponent } from '@app/modules/follow-ups/components/follow-ups-create-dialog/follow-ups-create-dialog.component';
import { FollowUpsListDialogComponent } from '@app/modules/follow-ups/components/follow-ups-list-dialog/follow-ups-list-dialog.component';
import {
  FollowUp,
  FollowUpLinkType,
  FollowUpStatus,
} from '@app/modules/follow-ups/follow-ups.types';
import {
  ArchiveFollowUpsAction,
  DeleteFollowUpAction,
  ResolveFollowUpsAction,
} from '@app/modules/follow-ups/store/follow-ups.actions';
import { FollowUpsFilters } from '@app/modules/follow-ups/store/follow-ups.state';
import { KitsUtilsService } from '@app/modules/kits/kits-utils.service';
import { ReportsUtilsService } from '@app/modules/reports/services/reports-utils.service';
import { ResultsUtilsService } from '@app/modules/reports/services/results-utils.service';
import { SampleUtilsService } from '@app/modules/service-data/services/sample-utils.service';
import { ServiceDataUtilsService } from '@app/modules/service-data/services/service-data-utils.service';
import {
  ExportDialogDataType,
  ExportType,
  ExportTypeNames,
} from '@app/modules/service-data/types/service-data.types';
import { RuntimeStatus } from '@core/core.types';
import { ExportersUtilsService } from '@core/services/exporters-utils.service';
import { HttpUtils } from '@core/utils/http-utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ConfirmService } from '@shared/components/confirm/confirm-dialog.component';
import { LoadingService } from '@shared/components/loading-dialog/loading-dialog.component';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { ConfirmCloseReason } from '@shared/shared.types';

@Injectable({
  providedIn: 'root',
})
export class FollowUpsUtilsService {
  private ngbModal = inject(NgbModal);
  private confirmService = inject(ConfirmService);
  private store = inject(Store);
  private sampleUtils = inject(SampleUtilsService);
  private kitsUtils = inject(KitsUtilsService);
  private exporterUtilsService = inject(ExportersUtilsService);
  private loadingService = inject(LoadingService);
  private translateService = inject(TranslateService);
  private toastService = inject(ToastService);
  private serviceDataUtils = inject(ServiceDataUtilsService);
  private reportUtils = inject(ReportsUtilsService);
  private resultUtils = inject(ResultsUtilsService);

  openFollowUpsListDialog(followUpIds: string[]) {
    const modalRef = this.ngbModal.open(FollowUpsListDialogComponent, {
      centered: true,
      modalDialogClass: 'modal-size-md',
    });

    modalRef.componentInstance.followUpIds = followUpIds;
  }

  setNotes(event, followUp) {
    const modalRef = this.ngbModal.open(FollowUpSetNotesComponent, {
      centered: true,
      modalDialogClass: 'modal-size-md',
    });

    modalRef.componentInstance.followUp = followUp;
  }

  setAssignee(event, followUps) {
    const modalRef = this.ngbModal.open(FollowUpSetAssigneeComponent, {
      centered: true,
      modalDialogClass: 'modal-size-md',
    });

    modalRef.componentInstance.followUps = followUps;
  }

  canResolveFollowUps(followUps) {
    return (
      !followUps.length || followUps.some((followUp) => !followUp.assignee)
    );
  }

  resolveFollowUps(event, followUps) {
    this.confirmService
      .confirm({
        title: this.translateService.instant('followUps.confirm.resolve.title'),
        message: this.translateService.instant(
          'followUps.confirm.resolve.message',
          {
            count: followUps.length,
          }
        ),
      })
      .subscribe((response) => {
        if (response.closeReason === ConfirmCloseReason.Yes) {
          this.loadingService.wait({
            message: this.translateService.instant('followUps.loading.resolve'),
            observableToFinish$: this.store.dispatch(
              new ResolveFollowUpsAction(
                followUps.map((followUp) => followUp.id)
              )
            ),
          });
        }
      });
  }

  deleteFollowUp(event, followUp: FollowUp) {
    this.confirmService
      .confirm({
        title: this.translateService.instant('followUps.confirm.delete.title'),
        message: this.translateService.instant(
          'followUps.confirm.delete.message'
        ),
      })
      .subscribe((response) => {
        if (response.closeReason === ConfirmCloseReason.Yes) {
          this.loadingService.wait({
            message: this.translateService.instant('followUps.loading.delete'),
            observableToFinish$: this.store.dispatch(
              new DeleteFollowUpAction(followUp.id)
            ),
          });
        }
      });
  }

  archiveFollowUps(event, followUps) {
    this.confirmService
      .confirm({
        title: this.translateService.instant('followUps.confirm.archive.title'),
        message: this.translateService.instant(
          'followUps.confirm.archive.message',
          {
            count: followUps.length,
          }
        ),
      })
      .subscribe((response) => {
        if (response.closeReason === ConfirmCloseReason.Yes) {
          this.loadingService.wait({
            message: this.translateService.instant('followUps.loading.archive'),
            observableToFinish$: this.store.dispatch(
              new ArchiveFollowUpsAction(
                followUps.map((followUp) => followUp.id)
              )
            ),
          });

          followUps.forEach((followUp) => {
            if (followUp.lastStatus === FollowUpStatus.Resolved) {
              this.toastService.warning(
                this.translateService.instant(
                  'followUps.warnings.cannotArchiveResolvedFollowUpMessage',
                  {
                    id: followUp.id,
                  }
                ),
                this.translateService.instant(
                  'followUps.warnings.cannotArchiveResolvedFollowUpTitle'
                )
              );
            }
          });
        }
      });
  }

  openDetails(event, followUp: FollowUp) {
    const modalRef = this.ngbModal.open(FollowUpDetailsComponent, {
      centered: true,
      modalDialogClass: 'modal-size-md',
    });

    modalRef.componentInstance.followUpId = followUp.id;
  }

  openCreateFollowUps(
    linkIds: string[],
    linkType: FollowUpLinkType,
    entities: { id: string }[]
  ) {
    const modalRef = this.ngbModal.open(FollowUpsCreateDialogComponent, {
      centered: true,
      modalDialogClass: 'modal-size-md',
    });

    modalRef.componentInstance.entities = entities;
    modalRef.componentInstance.linkIds = linkIds;
    modalRef.componentInstance.linkType = linkType;
  }

  openLinkDetails(event, followUp: FollowUp) {
    if (followUp.link.type === FollowUpLinkType.Sample) {
      this.sampleUtils.openDetails(followUp.link.id);
    } else if (followUp.link.type === FollowUpLinkType.Kit) {
      this.kitsUtils.openKitDetails(followUp.link.id);
    } else if (followUp.link.type === FollowUpLinkType.ServiceData) {
      this.serviceDataUtils.openDetails(followUp.link.id);
    } else if (followUp.link.type === FollowUpLinkType.Report) {
      this.reportUtils.openReportDetailsDialog(followUp.link.id);
    } else if (followUp.link.type === FollowUpLinkType.Result) {
      this.resultUtils.openResultDetails(followUp.link.id);
    }
  }

  openFollowUpsSearch() {
    this.ngbModal.open(FollowUpSearchComponent, {
      centered: true,
      modalDialogClass: 'modal-size-md',
    });
  }

  export(filters: FollowUpsFilters) {
    const progressSubject = this.exporterUtilsService.openProgressDialog(
      ExportTypeNames.FollowUps
    );

    const httpParams = this.buildHttpParams(filters);
    const paramsObject: { [key: string]: string } = {};
    httpParams.keys().forEach((key) => {
      paramsObject[key] = httpParams.get(key);
    });

    this.exporterUtilsService
      .export(paramsObject, ExportType.FollowUps)
      .subscribe((status) => {
        if (status !== RuntimeStatus.Running) {
          progressSubject.next({
            value: 100,
            status: this.translateService.instant('followUps.export.done'),
            success: status === RuntimeStatus.Completed,
          });
        }
      });
  }

  exportSelected(ids: string[]) {
    const progressSubject = this.exporterUtilsService.openProgressDialog(
      ExportType.FollowUps
    );

    this.exporterUtilsService
      .exportSelected(ExportDialogDataType.FollowUps, ids, ExportType.FollowUps)
      .subscribe((status) => {
        if (status !== RuntimeStatus.Running) {
          progressSubject.next({
            value: 100,
            status: this.translateService.instant('followUps.export.done'),
            success: status === RuntimeStatus.Completed ? true : false,
          });
        }
      });
  }

  buildHttpParams(params: FollowUpsFilters) {
    let httpParams = HttpUtils.buildHttpParams(params);

    if (params.ids?.length) {
      httpParams = httpParams.set('ids', params.ids.join(','));
    }

    if (params.assignees?.length) {
      httpParams = httpParams.set('assignees', params.assignees.join(','));
    }

    if (params.lastStatuses?.length) {
      httpParams = httpParams.set(
        'lastStatuses',
        params.lastStatuses.join(',')
      );
    }

    return httpParams;
  }
}
